// LocalTimeWeatherWidget
// -----------------------------------------------------------------------------
// Premium hospitality-style widget that surfaces, for a given property:
//   • Current local time in Morocco (Africa/Casablanca), ticking in real time
//   • Current weather pulled live from Open-Meteo using the property's exact
//     coordinates (no API key required, free tier)
//   • Today's high / low, humidity, wind, sunrise & sunset
//   • Contextual desert note for bivouacs (cold desert nights, dawn rituals)
//
// Designed to slot under the "Highlights / Quick facts" block on every hotel
// & bivouac detail page. Matches the Xaluca palette (#FDFBF7, #D4A373, #C16542,
// #2C2621) and the editorial typographic system (font-serif-x, overline).
//
// Data source: https://open-meteo.com (free, attribution friendly).

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Clock, Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain,
  CloudSnow, CloudLightning, Sunrise, Sunset, Droplets, Wind,
  ThermometerSun, ThermometerSnowflake, Compass,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getHotelLocation } from "@/lib/hotelLocations";
import { getHotelDetails } from "@/lib/hotelDetails";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// WMO Weather interpretation code mapping (Open-Meteo).
// https://open-meteo.com/en/docs#weathervariables
const WEATHER_CODE_MAP = {
  0:  { Icon: Sun,            label: { en: "Clear sky",       fr: "Ciel dégagé",          es: "Cielo despejado" } },
  1:  { Icon: Sun,            label: { en: "Mainly clear",    fr: "Ciel peu nuageux",     es: "Mayormente despejado" } },
  2:  { Icon: CloudSun,       label: { en: "Partly cloudy",   fr: "Partiellement nuageux", es: "Parcialmente nublado" } },
  3:  { Icon: Cloud,          label: { en: "Overcast",        fr: "Couvert",              es: "Nublado" } },
  45: { Icon: CloudFog,       label: { en: "Fog",             fr: "Brouillard",           es: "Niebla" } },
  48: { Icon: CloudFog,       label: { en: "Freezing fog",    fr: "Brouillard givrant",   es: "Niebla helada" } },
  51: { Icon: CloudDrizzle,   label: { en: "Light drizzle",   fr: "Bruine légère",        es: "Llovizna ligera" } },
  53: { Icon: CloudDrizzle,   label: { en: "Drizzle",         fr: "Bruine",               es: "Llovizna" } },
  55: { Icon: CloudDrizzle,   label: { en: "Dense drizzle",   fr: "Bruine dense",         es: "Llovizna intensa" } },
  61: { Icon: CloudRain,      label: { en: "Light rain",      fr: "Pluie faible",         es: "Lluvia ligera" } },
  63: { Icon: CloudRain,      label: { en: "Rain",            fr: "Pluie",                es: "Lluvia" } },
  65: { Icon: CloudRain,      label: { en: "Heavy rain",      fr: "Pluie forte",          es: "Lluvia intensa" } },
  71: { Icon: CloudSnow,      label: { en: "Light snow",      fr: "Neige légère",         es: "Nieve ligera" } },
  73: { Icon: CloudSnow,      label: { en: "Snow",            fr: "Neige",                es: "Nieve" } },
  75: { Icon: CloudSnow,      label: { en: "Heavy snow",      fr: "Neige forte",          es: "Nevada intensa" } },
  80: { Icon: CloudRain,      label: { en: "Rain showers",    fr: "Averses",              es: "Chubascos" } },
  81: { Icon: CloudRain,      label: { en: "Rain showers",    fr: "Averses",              es: "Chubascos" } },
  82: { Icon: CloudRain,      label: { en: "Heavy showers",   fr: "Averses fortes",       es: "Chubascos fuertes" } },
  95: { Icon: CloudLightning, label: { en: "Thunderstorm",    fr: "Orage",                es: "Tormenta" } },
  96: { Icon: CloudLightning, label: { en: "Thunderstorm",    fr: "Orage",                es: "Tormenta" } },
  99: { Icon: CloudLightning, label: { en: "Severe storm",    fr: "Orage violent",        es: "Tormenta severa" } },
};

const fallback = { Icon: Cloud, label: { en: "Conditions", fr: "Conditions", es: "Condiciones" } };

const COPY = {
  overline:   { en: "Local time & weather",     fr: "Heure locale & météo",        es: "Hora local y clima" },
  current:    { en: "Current conditions",       fr: "Conditions actuelles",        es: "Condiciones actuales" },
  high:       { en: "High today",               fr: "Max. du jour",                es: "Máx. hoy" },
  low:        { en: "Low today",                fr: "Min. du jour",                es: "Mín. hoy" },
  humidity:   { en: "Humidity",                 fr: "Humidité",                    es: "Humedad" },
  wind:       { en: "Wind",                     fr: "Vent",                        es: "Viento" },
  sunrise:    { en: "Sunrise",                  fr: "Lever du soleil",             es: "Amanecer" },
  sunset:     { en: "Sunset",                   fr: "Coucher du soleil",           es: "Atardecer" },
  morocco:    { en: "Morocco · Africa/Casablanca", fr: "Maroc · Africa/Casablanca", es: "Marruecos · Africa/Casablanca" },
  loading:    { en: "Loading conditions…",      fr: "Chargement des conditions…",  es: "Cargando condiciones…" },
  offline:    { en: "Weather unavailable right now — try again in a moment.", fr: "Météo indisponible — réessayez dans un instant.", es: "Clima no disponible — inténtelo de nuevo." },
  desert_note: {
    en: "Desert tip — pack a light layer for the cold nights and aim for the dunes 30 min before sunrise or sunset for the best light.",
    fr: "Conseil désert — prévoyez une petite laine pour les nuits fraîches et rejoignez les dunes 30 min avant le lever ou le coucher du soleil pour la plus belle lumière.",
    es: "Consejo del desierto — lleve una capa ligera para las noches frescas y suba a las dunas 30 min antes del amanecer o atardecer para la mejor luz.",
  },
};

// Build the Open-Meteo URL — current + today's daily aggregates.
const buildUrl = (lat, lng) => {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    current: "temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m",
    daily: "temperature_2m_max,temperature_2m_min,sunrise,sunset",
    timezone: "Africa/Casablanca",
    wind_speed_unit: "kmh",
    temperature_unit: "celsius",
    forecast_days: "1",
  });
  return `https://api.open-meteo.com/v1/forecast?${params.toString()}`;
};

// Format an ISO timestamp like "2026-02-19T07:14" as "07:14" in Morocco time.
const formatLocalTimeFromIso = (iso) => {
  if (!iso) return "—";
  const parts = iso.split("T");
  if (parts.length < 2) return iso;
  return parts[1].slice(0, 5);
};

const LocalTimeWeatherWidget = ({ hotelSlug, hotelName, isBivouac, testIdPrefix = "hotel" }) => {
  const { lang } = useLanguage();

  const loc = useMemo(() => {
    const d = getHotelDetails(hotelSlug);
    return getHotelLocation(hotelSlug, { location: d?.location || hotelName });
  }, [hotelSlug, hotelName]);

  const [lat, lng] = loc.coords;

  // ── Live Morocco clock ──────────────────────────────────────────────────
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const moroccoTime = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-GB", {
        timeZone: "Africa/Casablanca",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(now);
    } catch {
      return "—:—:—";
    }
  }, [now, lang]);

  const moroccoDate = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-GB", {
        timeZone: "Africa/Casablanca",
        weekday: "long",
        day: "numeric",
        month: "long",
      }).format(now);
    } catch {
      return "";
    }
  }, [now, lang]);

  // ── Live weather (Open-Meteo) ───────────────────────────────────────────
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "error"
  const lastFetchRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(buildUrl(lat, lng), { cache: "no-store" });
        if (!res.ok) throw new Error("network");
        const data = await res.json();
        if (cancelled) return;
        setWeather(data);
        setStatus("ok");
        lastFetchRef.current = Date.now();
      } catch {
        if (cancelled) return;
        setStatus("error");
      }
    };

    setStatus("loading");
    load();
    // Re-fetch every 10 minutes while the widget is mounted.
    const id = window.setInterval(load, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [lat, lng]);

  const code = weather?.current?.weather_code;
  const cond = WEATHER_CODE_MAP[code] || fallback;
  const Icon = cond.Icon;
  const condLabel = pick(cond.label, lang);

  const current = weather?.current;
  const daily = weather?.daily;

  const sunrise = daily?.sunrise?.[0];
  const sunset = daily?.sunset?.[0];
  const tMax = daily?.temperature_2m_max?.[0];
  const tMin = daily?.temperature_2m_min?.[0];
  const tNow = current?.temperature_2m;
  const humidity = current?.relative_humidity_2m;
  const wind = current?.wind_speed_10m;

  const round = (v) => (typeof v === "number" ? Math.round(v) : "—");

  return (
    <section
      className="bg-[#FDFBF7] py-16 md:py-20 border-b border-[#2C2621]/8"
      data-testid="section-local-time-weather"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 items-end mb-10 md:mb-12">
          <div className="md:col-span-7">
            <p className="overline">{pick(COPY.overline, lang)}</p>
            <h2 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#2C2621] leading-[1.05]">
              {pick(COPY.current, lang)}
            </h2>
          </div>
          <div className="md:col-span-5 md:justify-self-end text-[#5C5248] text-sm">
            <p className="tabular-nums tracking-tight">{hotelName} · {lat.toFixed(3)}°N · {Math.abs(lng).toFixed(3)}°{lng < 0 ? "W" : "E"}</p>
            <p className="text-[11px] tracking-[0.22em] uppercase mt-1 text-[#5C5248]/70">{pick(COPY.morocco, lang)}</p>
          </div>
        </div>

        {/* Card */}
        <div className="grid md:grid-cols-12 gap-6 lg:gap-8" data-testid={`${testIdPrefix}-weather-card`}>
          {/* Time block — left column */}
          <div
            className="md:col-span-5 relative overflow-hidden bg-[#2C2621] text-[#FDFBF7] p-7 md:p-9 min-h-[260px] flex flex-col justify-between"
            data-testid={`${testIdPrefix}-weather-time`}
          >
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.7} />
                {pick({ en: "Local time", fr: "Heure locale", es: "Hora local" }, lang)}
              </div>
              <p
                className="font-serif-x mt-6 text-6xl md:text-7xl tracking-tight tabular-nums leading-none"
                data-testid={`${testIdPrefix}-weather-clock`}
              >
                {moroccoTime}
              </p>
              <p className="mt-3 text-sm text-[#FDFBF7]/75 capitalize">{moroccoDate}</p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 text-[11px] tracking-[0.22em] uppercase text-[#FDFBF7]/65">
              <div className="flex items-center gap-2">
                <Sunrise className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={1.7} />
                <span className="text-[#FDFBF7]">{formatLocalTimeFromIso(sunrise)}</span>
                <span className="hidden sm:inline">· {pick(COPY.sunrise, lang)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Sunset className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={1.7} />
                <span className="text-[#FDFBF7]">{formatLocalTimeFromIso(sunset)}</span>
                <span className="hidden sm:inline">· {pick(COPY.sunset, lang)}</span>
              </div>
            </div>
            {/* Decorative diamond */}
            <span
              aria-hidden="true"
              className="absolute top-6 right-6 w-3 h-3 rotate-45 border border-[#D4A373]/60"
            />
          </div>

          {/* Weather block — right column */}
          <div
            className="md:col-span-7 bg-[#F2EBE1]/55 border border-[#D4A373]/25 p-7 md:p-9 min-h-[260px] flex flex-col"
            data-testid={`${testIdPrefix}-weather-conditions`}
          >
            {status === "loading" && (
              <div className="m-auto text-[#5C5248] text-sm tracking-wide" data-testid={`${testIdPrefix}-weather-loading`}>
                {pick(COPY.loading, lang)}
              </div>
            )}

            {status === "error" && (
              <div className="m-auto text-[#5C5248] text-sm tracking-wide" data-testid={`${testIdPrefix}-weather-error`}>
                {pick(COPY.offline, lang)}
              </div>
            )}

            {status === "ok" && current && (
              <>
                <div className="flex items-start gap-5">
                  <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#FDFBF7] border border-[#D4A373]/40 flex items-center justify-center">
                    <Icon className="w-9 h-9 md:w-10 md:h-10 text-[#C16542]" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p
                      className="font-serif-x text-5xl md:text-6xl text-[#2C2621] leading-none tabular-nums"
                      data-testid={`${testIdPrefix}-weather-temp`}
                    >
                      {round(tNow)}°<span className="text-2xl md:text-3xl align-top text-[#5C5248]">C</span>
                    </p>
                    <p className="mt-2 text-[#2C2621] text-base md:text-lg" data-testid={`${testIdPrefix}-weather-condition`}>
                      {condLabel}
                    </p>
                    <p className="mt-1 text-[11px] tracking-[0.22em] uppercase text-[#5C5248]/80">
                      {pick({ en: "Updated just now", fr: "Mis à jour à l'instant", es: "Actualizado ahora" }, lang)}
                    </p>
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4 text-[#2C2621]">
                  <Metric icon={ThermometerSun}     label={pick(COPY.high, lang)}     value={`${round(tMax)}°`} testId={`${testIdPrefix}-weather-high`} />
                  <Metric icon={ThermometerSnowflake} label={pick(COPY.low, lang)}    value={`${round(tMin)}°`} testId={`${testIdPrefix}-weather-low`} />
                  <Metric icon={Droplets}            label={pick(COPY.humidity, lang)} value={`${round(humidity)}%`} testId={`${testIdPrefix}-weather-humidity`} />
                  <Metric icon={Wind}                label={pick(COPY.wind, lang)}     value={`${round(wind)} km/h`} testId={`${testIdPrefix}-weather-wind`} />
                </div>

                {isBivouac && (
                  <div
                    className="mt-7 flex items-start gap-3 border-t border-[#D4A373]/30 pt-5 text-[13px] text-[#5C5248] leading-relaxed"
                    data-testid={`${testIdPrefix}-weather-desert-note`}
                  >
                    <Compass className="w-4 h-4 mt-0.5 text-[#C16542] shrink-0" strokeWidth={1.6} />
                    <p className="italic">{pick(COPY.desert_note, lang)}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Metric = ({ icon: Icon, label, value, testId }) => (
  <div className="flex flex-col gap-1.5" data-testid={testId}>
    <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/80">
      <Icon className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.7} />
      {label}
    </div>
    <div className="font-serif-x text-2xl md:text-3xl leading-tight tabular-nums">{value}</div>
  </div>
);

export default LocalTimeWeatherWidget;
